// "Your model library" on Admin → Model Serving, laid out for the width it actually
// has (measured, not the window): the full table when there's room, a condensed
// table when it's tighter, one card per model when it's narrow. No fixed pixel
// widths — the old table forced 760px and scrolled sideways on a narrow screen.
// Every layout keeps the same markers (row-<key>, data-lc, ms-badge).
import { COLORS } from "../../theme/colors";
import { modelStatus, presetLabel, rowActions, versionText, weightsCell } from "./modelServingModel";
import { Badge, Btn, MONO, Progress } from "./modelServingUi";

// Widths (px of the library itself) where the layout steps down.
export const LIBRARY_BREAKPOINTS = { table: 900, compact: 600 };

/** "table" | "compact" | "cards" for a library this wide. Unmeasured (0) → "table". */
export function libraryLayout(width) {
  if (!width || width >= LIBRARY_BREAKPOINTS.table) return "table";
  return width >= LIBRARY_BREAKPOINTS.compact ? "compact" : "cards";
}

export const ACTION = {
  details: ["Details", "default", undefined], stop: ["Stop", "danger", "stop"], warm: ["▶ Warm up", "primary", "warm-up"],
  cancel_download: ["Cancel download", "danger", "cancel-download"], remove: ["Remove", "default", undefined],
  retry_download: ["Retry", "primary", "retry-download"], retry_warm: ["Try again", "primary", "retry-warm"],
};

const HEADERS = {
  table: ["Model", "Hugging Face repo", "Version", "Weights in S3", "Status", "Actions"],
  compact: ["Model", "Weights in S3", "Status", "Actions"],
};

export function LibraryHead({ layout }) {
  return (
    <thead>
      <tr style={{ color: COLORS.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.6 }}>
        {HEADERS[layout].map((h) => (
          <th key={h} style={{ textAlign: h === "Actions" ? "right" : "left", padding: "11px 12px",
            borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
        ))}
      </tr>
    </thead>
  );
}

const td = { padding: "12px 12px", borderBottom: `1px solid ${COLORS.border}`, verticalAlign: "top" };
const repoStyle = { fontFamily: MONO, fontSize: 12, color: COLORS.textMuted, overflowWrap: "anywhere" };
const sub = { fontSize: 12, color: COLORS.textDim };

function Weights({ model, status }) {
  const cell = weightsCell(model);
  return (
    <>
      {cell.text}
      {cell.progress != null && <Progress pct={cell.progress} />}
      {cell.meta && <div data-lc="download-progress" style={{ fontSize: 11.5, color: COLORS.textDim }}>{cell.meta}</div>}
      {cell.error && (
        <div data-lc={status === "warm_failed" ? "warm-error" : "download-error"}
          style={{ fontSize: 11.5, color: COLORS.pink, marginTop: 3 }}>{cell.error}</div>
      )}
    </>
  );
}

function Actions({ status, model, onAction }) {
  return (
    <div style={{ display: "inline-flex", gap: 7, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {rowActions(status, model).map((a) => (
        <Btn key={a} sm kind={ACTION[a][1]} data-lc={ACTION[a][2]} onClick={() => onAction(a, model)}>{ACTION[a][0]}</Btn>
      ))}
    </div>
  );
}

/** One model of the library, in the given layout. */
export function LibraryRow({ model, preset, layout = "table", onAction }) {
  const status = modelStatus(model);
  const gpu = preset ? presetLabel(preset) : model.node_class || "—";
  const badge = <Badge status={status} lc={status === "parked" ? "parked-state" : undefined} />;
  const weights = <Weights model={model} status={status} />;
  const actions = <Actions status={status} model={model} onAction={onAction} />;
  if (layout === "cards") return <LibraryCard model={model} gpu={gpu} badge={badge} weights={weights} actions={actions} />;
  const name = <div style={{ fontWeight: 600 }}>{model.display_name}</div>;
  const gpuLine = <div style={{ ...sub, fontFamily: MONO }}>{gpu}</div>;
  if (layout === "compact") {
    return (
      <tr data-testid={`row-${model.key}`} data-layout="compact">
        <td style={td}>{name}{gpuLine}<div style={{ ...repoStyle, marginTop: 3 }}>{model.hf_repo || "—"} · {versionText(model)}</div></td>
        <td style={td}>{weights}</td>
        <td style={td}>{badge}</td>
        <td style={{ ...td, textAlign: "right" }}>{actions}</td>
      </tr>
    );
  }
  return (
    <tr data-testid={`row-${model.key}`} data-layout="table">
      <td style={td}>{name}{gpuLine}</td>
      <td style={{ ...td, ...repoStyle }}>{model.hf_repo || "—"}</td>
      <td style={{ ...td, fontFamily: MONO, fontSize: 12 }}>{versionText(model)}</td>
      <td style={td}>{weights}</td>
      <td style={td}>{badge}</td>
      <td style={{ ...td, textAlign: "right" }}>{actions}</td>
    </tr>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(80px, 30%) 1fr", gap: 10, alignItems: "start" }}>
      <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.6, color: COLORS.textDim }}>{label}</span>
      <div style={{ minWidth: 0, fontSize: 13 }}>{children}</div>
    </div>
  );
}

function LibraryCard({ model, gpu, badge, weights, actions }) {
  return (
    <div role="listitem" data-testid={`row-${model.key}`} data-layout="cards"
      style={{ display: "grid", gap: 10, padding: 14, borderBottom: `1px solid ${COLORS.border}` }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <b style={{ fontSize: 14 }}>{model.display_name}</b>{badge}
      </div>
      <Field label="GPU"><span style={{ fontFamily: MONO, fontSize: 12 }}>{gpu}</span></Field>
      <Field label="Repo"><span style={repoStyle}>{model.hf_repo || "—"}</span></Field>
      <Field label="Version"><span style={{ fontFamily: MONO, fontSize: 12 }}>{versionText(model)}</span></Field>
      <Field label="Weights in S3">{weights}</Field>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>{actions}</div>
    </div>
  );
}
